import { Link, router, usePage } from "@inertiajs/react"
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Textarea } from "@/components/ui/textarea"
import { LayoutList, Map, RefreshCw } from "lucide-react"
import { AdminLayout } from "@/layouts/AdminLayout"
import { MapListView } from "@/components/shared/MapListView"
import type { ContentItem, PaginationMeta, RegionOption } from "@/lib/types"
import type { SharedProps } from "@/types"

interface Props {
  items: ContentItem[]
  content_type: "pois" | "events"
  meta: PaginationMeta
  poi_count: number
  event_count: number
  stale_count: number
  regions: RegionOption[]
  categories: Array<{ id: number; name: string }>
  filters: Record<string, string | undefined>
  can_bulk_action: boolean
  urls: { index: string; bulk_action: string }
}

const STATUS_LABELS: Record<string, string> = {
  approved:      "Approved",
  pending:       "Pending",
  needs_changes: "Needs Changes",
  rejected:      "Rejected",
  draft:         "Draft",
  archived:      "Archived",
}

const STATUS_VARIANTS: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
  approved:      "default",
  pending:       "secondary",
  needs_changes: "outline",
  rejected:      "destructive",
  draft:         "secondary",
  archived:      "outline",
}

const MAX_VISIBLE_CATEGORIES = 3

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface FiltersProps {
  filters: Record<string, string | undefined>
  regions: RegionOption[]
  categories: Array<{ id: number; name: string }>
  content_type: "pois" | "events"
  isDistributor: boolean
  hasActiveFilters: boolean
  searchValue: string
  setSearchValue: (v: string) => void
  handleSearch: (v: string) => void
  visit: (overrides: Record<string, string | undefined>) => void
  clearFilters: () => void
  staleCount: number
}

function ContentFilters({
  filters,
  regions,
  categories,
  content_type,
  isDistributor,
  hasActiveFilters,
  searchValue,
  setSearchValue,
  handleSearch,
  visit,
  clearFilters,
  staleCount,
}: FiltersProps) {
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <Label htmlFor="region-filter" className="sr-only">Region</Label>
      <Select
        value={filters.region_id ?? "any"}
        onValueChange={(v) => visit({ region_id: v === "any" ? undefined : v })}
      >
        <SelectTrigger id="region-filter" className="w-40">
          <SelectValue placeholder="All Regions" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All Regions</SelectItem>
          {regions.map(({ id, name }) => (
            <SelectItem key={id} value={String(id)}>
              {name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!isDistributor && (
        <>
          <Label htmlFor="visibility-filter" className="sr-only">Visibility</Label>
          <Select
            value={filters.visibility ?? "any"}
            onValueChange={(v) => visit({ visibility: v === "any" ? undefined : v })}
          >
            <SelectTrigger id="visibility-filter" className="w-36">
              <SelectValue placeholder="Visibility" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All</SelectItem>
              <SelectItem value="public">Public</SelectItem>
              <SelectItem value="private">Private</SelectItem>
            </SelectContent>
          </Select>
        </>
      )}

      <Label htmlFor="status-filter" className="sr-only">Status</Label>
      <Select
        value={filters.status ?? "any"}
        onValueChange={(v) => visit({ status: v === "any" ? undefined : v })}
      >
        <SelectTrigger id="status-filter" className="w-40">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">All Statuses</SelectItem>
          {Object.entries(STATUS_LABELS).map(([val, label]) => (
            <SelectItem key={val} value={val}>
              {label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {content_type === "pois" && (
        <>
          <Label htmlFor="category-filter" className="sr-only">Category</Label>
          <Select
            value={filters.category_id ?? "any"}
            onValueChange={(v) => visit({ category_id: v === "any" ? undefined : v })}
          >
            <SelectTrigger id="category-filter" className="w-44">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">All Categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={String(c.id)}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </>
      )}

      {content_type === "events" && (
        <>
          <Label htmlFor="show-cancelled-filter" className="sr-only">Include cancelled</Label>
          <label className="flex items-center gap-1.5 text-sm text-muted-foreground cursor-pointer">
            <Checkbox
              id="show-cancelled-filter"
              checked={filters.show_cancelled === "true"}
              onCheckedChange={(checked) =>
                visit({ show_cancelled: checked ? "true" : undefined })
              }
            />
            Include cancelled
          </label>
        </>
      )}

      <Label htmlFor="content-search" className="sr-only">Search by name</Label>
      <Input
        id="content-search"
        placeholder="Search..."
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value)
          handleSearch(e.target.value)
        }}
        className="w-48"
      />

      {content_type === "pois" && (
        <button
          type="button"
          onClick={() => visit({ stale: filters.stale === "true" ? undefined : "true" })}
          className={[
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm font-medium transition-colors",
            filters.stale === "true"
              ? "border-amber-400 bg-amber-50 text-amber-700"
              : "border-border bg-background text-muted-foreground hover:border-amber-300 hover:text-amber-600",
          ].join(" ")}
        >
          <RefreshCw className="size-3.5" />
          Stale only
          {staleCount > 0 && (
            <span className="ml-0.5 rounded-full bg-amber-200 px-1.5 py-0.5 text-xs font-semibold text-amber-800">
              {staleCount}
            </span>
          )}
        </button>
      )}

      {hasActiveFilters && (
        <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
          Clear filters
        </Button>
      )}
    </div>
  )
}

interface TableProps {
  items: ContentItem[]
  content_type: "pois" | "events"
  isDistributor: boolean
  isAdmin: boolean | undefined
  canBulkAction: boolean
  colCount: number
  hasActiveFilters: boolean
  selectedIds: Set<number>
  toggleSelect: (id: number) => void
  selectAll: () => void
  clearFilters: () => void
  verifyingIds: Set<number>
  onVerify: (item: ContentItem) => void
}

function ContentTable({
  items,
  content_type,
  isDistributor,
  isAdmin,
  canBulkAction,
  colCount,
  hasActiveFilters,
  selectedIds,
  toggleSelect,
  selectAll,
  clearFilters,
  verifyingIds,
  onVerify,
}: TableProps) {
  const canModerate = (item: ContentItem) => isAdmin && item.moderation_review_id !== null
  const allSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id))

  return (
    <div className="rounded-md border overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            {canBulkAction && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={() => (allSelected ? undefined : selectAll())}
                  aria-label="Select all on this page"
                />
              </TableHead>
            )}
            <TableHead>Name</TableHead>
            <TableHead>Region</TableHead>
            <TableHead>Status</TableHead>
            {!isDistributor && <TableHead>Visibility</TableHead>}
            {content_type === "pois" && <TableHead>Categories</TableHead>}
            {content_type === "events" && <TableHead>Date</TableHead>}
            {isAdmin && <TableHead className="w-32">Actions</TableHead>}
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={colCount}
                className="text-center text-muted-foreground py-8"
              >
                {hasActiveFilters ? (
                  <>
                    No {content_type} match current filters.{" "}
                    <button
                      type="button"
                      className="underline hover:no-underline"
                      onClick={clearFilters}
                    >
                      Reset filters
                    </button>
                  </>
                ) : (
                  <>
                    No {content_type} yet.{" "}
                    {content_type === "pois"
                      ? "Add POIs via Properties to see them here."
                      : "Events created under Properties will appear here."}
                  </>
                )}
              </TableCell>
            </TableRow>
          ) : (
            items.map((item) => (
              <TableRow key={item.id} data-state={selectedIds.has(item.id) ? "selected" : undefined}>
                {canBulkAction && (
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(item.id)}
                      onCheckedChange={() => toggleSelect(item.id)}
                      aria-label={`Select ${item.name}`}
                    />
                  </TableCell>
                )}
                <TableCell className="font-medium">
                  <div className={["flex items-center gap-2", verifyingIds.has(item.id) ? "opacity-50" : ""].join(" ").trim()}>
                    {item.name}
                    {item.kind === "poi" && item.stale && (
                      <>
                        <span className="inline-flex items-center rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-600">
                          Stale
                        </span>
                        <button
                          type="button"
                          aria-label="Mark as verified"
                          disabled={!item.verify_url || verifyingIds.has(item.id)}
                          onClick={() => onVerify(item)}
                          className="text-amber-600 hover:text-amber-800 disabled:cursor-not-allowed"
                        >
                          <RefreshCw className="size-4" />
                        </button>
                      </>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {item.region_name ?? "—"}
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_VARIANTS[item.status] ?? "secondary"}>
                    {STATUS_LABELS[item.status] ?? item.status}
                  </Badge>
                  {item.kind === "event" && item.cancelled && (
                    <Badge variant="destructive" className="ml-1">
                      Cancelled
                    </Badge>
                  )}
                </TableCell>
                {!isDistributor && (
                  <TableCell className="text-sm capitalize">{item.visibility}</TableCell>
                )}
                {content_type === "pois" && (
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(item.categories ?? []).slice(0, MAX_VISIBLE_CATEGORIES).map((c) => (
                        <Badge key={c.id} variant="outline" className="text-xs">
                          {c.name}
                        </Badge>
                      ))}
                      {(item.categories ?? []).length > MAX_VISIBLE_CATEGORIES && (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          +{(item.categories ?? []).length - MAX_VISIBLE_CATEGORIES} more
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                )}
                {content_type === "events" && (
                  <TableCell className="text-sm text-muted-foreground">
                    {item.starts_at
                      ? new Date(item.starts_at).toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                      : "—"}
                  </TableCell>
                )}
                {isAdmin && (
                  <TableCell>
                    <div className="flex gap-2 text-sm">
                      {canModerate(item) && item.moderation_url && (
                        <Link
                          href={item.moderation_url}
                          className="text-primary underline hover:no-underline"
                        >
                          Moderate
                        </Link>
                      )}
                      {item.property_id && item.property_url && (
                        <Link
                          href={item.property_url}
                          className="text-muted-foreground underline hover:no-underline"
                        >
                          View
                        </Link>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

interface PaginationProps {
  meta: PaginationMeta
  visit: (overrides: Record<string, string | undefined>) => void
}

function ContentPagination({ meta, visit }: PaginationProps) {
  if (meta.pages <= 1) return null

  return (
    <div className="flex items-center justify-between text-sm text-muted-foreground">
      <span>
        Page {meta.page} of {meta.pages} ({meta.count} total)
      </span>
      <div className="flex gap-2">
        {meta.page > 1 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => visit({ page: String(meta.page - 1) })}
          >
            Previous
          </Button>
        )}
        {meta.page < meta.pages && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => visit({ page: String(meta.page + 1) })}
          >
            Next
          </Button>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------

function ContentIndex({
  items,
  content_type,
  meta,
  poi_count,
  event_count,
  stale_count,
  regions,
  categories,
  filters,
  can_bulk_action,
  urls,
}: Props) {
  const { props: sharedProps } = usePage<SharedProps>()
  const isAdmin = sharedProps.user_context?.is_admin
  const isDistributor =
    !sharedProps.user_context?.is_admin &&
    !sharedProps.user_context?.is_support &&
    (sharedProps.user_context?.managed_region_ids?.length ?? 0) > 0

  // Checkbox column adds 1 when bulk actions are available
  const colCount =
    3 + (!isDistributor ? 1 : 0) + 1 + (isAdmin ? 1 : 0) + (can_bulk_action ? 1 : 0)

  const [viewMode, setViewMode] = useState<"list" | "map">("list")

  // Selection state — cleared whenever the items list changes (page nav, filter, tab switch)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  useEffect(() => {
    setSelectedIds(new Set())
  }, [items])

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }
  const selectAll = () => setSelectedIds(new Set(items.map((i) => i.id)))
  const clearSelection = () => setSelectedIds(new Set())

  // Reject dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false)
  const [rejectNotes, setRejectNotes] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)

  function submitBulkAction(decision: "approved" | "rejected", notes?: string) {
    setIsSubmitting(true)
    router.post(
      urls.bulk_action,
      { ids: [...selectedIds], content_type, decision, notes },
      {
        onSuccess: () => {
          clearSelection()
          setRejectDialogOpen(false)
          setRejectNotes("")
        },
        onFinish: () => setIsSubmitting(false),
      },
    )
  }

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [searchValue, setSearchValue] = useState(filters.search ?? "")
  useEffect(() => {
    setSearchValue(filters.search ?? "")
  }, [filters.search])

  const visit = useCallback(
    (overrides: Record<string, string | undefined>) => {
      router.get(
        urls.index,
        { ...filters, ...overrides },
        { preserveState: true, replace: true },
      )
    },
    [filters, urls.index],
  )

  const hasActiveFilters = Object.entries(filters).some(
    ([key, val]) => key !== "content_type" && val !== undefined && val !== "",
  )

  const handleSearch = (value: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current)
    if (value.length > 0 && value.length < 3) return
    searchTimeout.current = setTimeout(() => {
      visit({ search: value || undefined })
    }, 300)
  }

  const clearFilters = () => {
    visit({
      region_id:      undefined,
      visibility:     undefined,
      status:         undefined,
      category_id:    undefined,
      search:         undefined,
      show_cancelled: undefined,
      stale:          undefined,
    })
  }

  // Verify (mark as verified) — per-row in-flight tracking
  const [verifyingIds, setVerifyingIds] = useState<Set<number>>(new Set())

  const handleVerify = (item: ContentItem) => {
    const verifyUrl = item.verify_url
    if (!verifyUrl && import.meta.env.DEV) {
      console.error(`Missing verify_url for POI ${item.id}`)
    }
    if (!verifyUrl) return
    setVerifyingIds((prev) => new Set(prev).add(item.id))
    router.post(verifyUrl, {}, {
      preserveState: true,
      onFinish: () => {
        setVerifyingIds((prev) => {
          const next = new Set(prev)
          next.delete(item.id)
          return next
        })
      },
    })
  }

  const tableView = (
    <ContentTable
      items={items}
      content_type={content_type}
      isDistributor={isDistributor}
      isAdmin={isAdmin}
      canBulkAction={can_bulk_action}
      colCount={colCount}
      hasActiveFilters={hasActiveFilters}
      selectedIds={selectedIds}
      toggleSelect={toggleSelect}
      selectAll={selectAll}
      clearFilters={clearFilters}
      verifyingIds={verifyingIds}
      onVerify={handleVerify}
    />
  )

  return (
    <div className="space-y-4 p-6">
      <div>
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight">Content</h1>
          {content_type === "pois" && stale_count > 0 && (
            <Badge className="border border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-50">
              {stale_count} stale {stale_count === 1 ? "POI" : "POIs"}
            </Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          All POIs and Events across managed regions
        </p>
      </div>

      {/* Tab bar + view toggle */}
      <div className="flex items-center justify-between gap-4">
        <Tabs
          value={content_type}
          onValueChange={(val) => {
            visit({ content_type: val as "pois" | "events", category_id: undefined })
            if (val === "events") setViewMode("list")
          }}
        >
          <TabsList>
            <TabsTrigger value="pois">POIs ({poi_count})</TabsTrigger>
            <TabsTrigger value="events">Events ({event_count})</TabsTrigger>
          </TabsList>
        </Tabs>

        {content_type === "pois" && (
          <div className="flex items-center gap-1 rounded-md border p-0.5">
            <Button
              variant={viewMode === "list" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2"
              onClick={() => setViewMode("list")}
              aria-label="List view"
            >
              <LayoutList className="size-4" />
            </Button>
            <Button
              variant={viewMode === "map" ? "secondary" : "ghost"}
              size="sm"
              className="h-7 px-2"
              onClick={() => setViewMode("map")}
              aria-label="Map view"
            >
              <Map className="size-4" />
            </Button>
          </div>
        )}
      </div>

      <ContentFilters
        filters={filters}
        regions={regions}
        categories={categories}
        content_type={content_type}
        isDistributor={isDistributor}
        hasActiveFilters={hasActiveFilters}
        searchValue={searchValue}
        setSearchValue={setSearchValue}
        handleSearch={handleSearch}
        visit={visit}
        clearFilters={clearFilters}
        staleCount={stale_count}
      />

      {/* Bulk action bar — admin/support only, visible when items are selected */}
      {can_bulk_action && selectedIds.size > 0 && (
        <div className="flex items-center gap-2 rounded-md border bg-muted px-3 py-2">
          <span className="text-sm font-medium">
            {selectedIds.size} {selectedIds.size === 1 ? "item" : "items"} selected
          </span>
          <Button
            size="sm"
            variant="default"
            disabled={isSubmitting}
            onClick={() => submitBulkAction("approved")}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={isSubmitting}
            onClick={() => setRejectDialogOpen(true)}
          >
            Reject
          </Button>
          <Button size="sm" variant="ghost" disabled={isSubmitting} onClick={clearSelection}>
            Clear
          </Button>
        </div>
      )}

      {viewMode === "map" && content_type === "pois" ? (
        <MapListView
          pins={items
            .filter((item) => item.kind === "poi" && item.lat != null && item.lng != null)
            .map((item) => ({
              id:       item.id,
              name:     item.name,
              lat:      item.lat!,
              lng:      item.lng!,
              category: item.categories?.[0]?.name ?? null,
            }))}
          center={null}
          className="rounded-md border"
        >
          {tableView}
        </MapListView>
      ) : (
        tableView
      )}

      <ContentPagination meta={meta} visit={visit} />

      {/* Reject notes dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Reject {selectedIds.size} {selectedIds.size === 1 ? "item" : "items"}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            The same note will be applied to all {selectedIds.size} selected{" "}
            {selectedIds.size === 1 ? "item" : "items"}.
          </p>
          <div className="grid gap-1.5">
            <Label htmlFor="reject-notes">Rejection notes *</Label>
            <Textarea
              id="reject-notes"
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Explain why this content is being rejected…"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectDialogOpen(false)
                setRejectNotes("")
              }}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              disabled={!rejectNotes.trim() || isSubmitting}
              onClick={() => submitBulkAction("rejected", rejectNotes)}
            >
              {isSubmitting ? "Rejecting…" : "Confirm rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

ContentIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>

export default ContentIndex
